import { useQueryClient } from "@tanstack/react-query";
import { useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import axiosAPI from "../../../axiosAPI";
import ModalAddIACredits from "../../../components/Modals/ModalAddIACredits";
import AuthContext from "../../../contexts/AuthContext/AuthContext";
import {
  OpenAIAPI,
  appendAssistantDeltaToCache,
  finalizeAssistantInCache,
  titleFromQuestion,
  useSendMessage,
  useStartChat,
} from "../hooks/useChatData";

function stringToFloat(value) {
  if (typeof value !== "string") return NaN;
  const normalized = value.replace(",", ".");
  return parseFloat(normalized);
}

const DEFAULT_MINIMUM_TOKENS_AI_BALANCE = 5;

export default function Composer({ selectedChatId, onChatCreated, isSidebarOpen, onToggleSidebar }) {
  const { user, updateUserTokensAi } = useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);

  const [text, setText] = useState("");
  const [type, setType] = useState("simple_question");
  const textareaRef = useRef(null);

  const startChat = useStartChat();
  const sendMessage = useSendMessage(selectedChatId);
  const qc = useQueryClient();

  const isSending = startChat.isLoading || sendMessage.isLoading;

  async function handleSend() {
    const currentTokensAi = stringToFloat(user?.tokens_ai || 0);
    if (currentTokensAi <= 0) {
      setShowModal(true);
      return;
    }

    const value = text.trim();
    if (!value) return;

    textareaRef.current?.blur();
    setText("");

    try {
      // ======== NOVO: vamos usar STREAM em ambos os casos ========
      if (!selectedChatId) {
        const now = Date.now();
        const tempId = `temp-${now}`;
        const tempUser = {
          id: `temp-user-${now}`,
          role: "user",
          content: value,
          status: "ok",
          __optimistic: true,
        };
        const tempAssistant = {
          id: `temp-assistant-${now}`,
          role: "assistant",
          content: "",
          status: "pending",
          __optimistic: true,
        };

        // cria chat temporário e seleciona
        const tempTitle = titleFromQuestion(value);
        qc.setQueryData(["chat", tempId], {
          id: tempId,
          title: tempTitle,
          messages: [tempUser, tempAssistant],
        });
        onChatCreated?.(tempId);

        let realChatId = null;

        await OpenAIAPI.startChatStream({
          question: value,
          type,
          title: tempTitle,
          onEvent: async (evt) => {
            if (evt?.event === "chat" && evt.chat_id) {
              // trocar temp pelo real
              realChatId = String(evt.chat_id);

              // clona cache do temp para o real
              const tempData = qc.getQueryData(["chat", tempId]);
              await qc.cancelQueries({ queryKey: ["chat", realChatId] });
              qc.setQueryData(
                ["chat", realChatId],
                tempData || {
                  id: realChatId,
                  title: evt.title || tempTitle,
                  messages: [],
                }
              );
              qc.removeQueries({ queryKey: ["chat", tempId] });
              onChatCreated?.(realChatId);
            }
            if (
              evt?.event === "balance" &&
              typeof evt.ai_balance_brl === "number"
            ) {
              updateUserTokensAi(evt.ai_balance_brl);
              if (evt.ai_balance_brl <= DEFAULT_MINIMUM_TOKENS_AI_BALANCE)
                setShowModal(true);
            }
            // opcional: evt.event === 'keepalive' / 'open' / 'done'
          },
          onDelta: (chunk) => {
            appendAssistantDeltaToCache(qc, realChatId || tempId, chunk);
          },
        });

        finalizeAssistantInCache(qc, realChatId || tempId);
        // ao final, invalida a lista para refletir título/status/updated_at
        await Promise.all([
          qc.invalidateQueries({ queryKey: ["chats"] }),
          qc.invalidateQueries({ queryKey: ["chat", (realChatId || tempId)] }),
        ]);
      } else {
        // chat existente
        // prepara UI otimista local (igual seu onMutate), mas aqui manualmente:
        const prev = qc.getQueryData(["chat", selectedChatId]);
        const now = Date.now();
        const tempUserId = `temp-user-${now}`;
        const tempAssistId = `temp-assistant-${now}`;
        const optimistic = prev
          ? {
              ...prev,
              messages: [
                ...(prev.messages ?? []),
                {
                  id: tempUserId,
                  role: "user",
                  content: value,
                  status: "ok",
                  __optimistic: true,
                },
                {
                  id: tempAssistId,
                  role: "assistant",
                  content: "",
                  status: "pending",
                  __optimistic: true,
                },
              ],
            }
          : prev;
        if (optimistic) qc.setQueryData(["chat", selectedChatId], optimistic);

        await OpenAIAPI.sendMessageStream(selectedChatId, {
          message: value,
          onEvent: (evt) => {
            if (
              evt?.event === "balance" &&
              typeof evt.ai_balance_brl === "number"
            ) {
              updateUserTokensAi(evt.ai_balance_brl);
              if (evt.ai_balance_brl <= DEFAULT_MINIMUM_TOKENS_AI_BALANCE)
                setShowModal(true);
            }
          },
          onDelta: (chunk) => {
            appendAssistantDeltaToCache(qc, selectedChatId, chunk);
          },
        });

        finalizeAssistantInCache(qc, selectedChatId);
        await Promise.all([
          qc.invalidateQueries({ queryKey: ["chats"] }),
          qc.invalidateQueries({ queryKey: ["chat", selectedChatId] }),
        ]);
      }
    } catch (e) {
      // rollback (chat novo temporário)
      const keys = qc
        .getQueryCache()
        .getAll()
        .map((q) => q.queryKey);
      const tempKey = keys.find(
        (k) =>
          Array.isArray(k) &&
          k[0] === "chat" &&
          String(k[1] || "").startsWith("temp-")
      );
      if (tempKey) {
        qc.removeQueries({ queryKey: tempKey });
        onChatCreated?.(null);
      }
      alert("Erro ao iniciar/enviar mensagem. Tente novamente.");
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function onSubmitModalAddIaCredits(values) {
    axiosAPI
      .post("openai/add/credits", values)
      .then((data) => {
        if (data.status === 200) {
          setShowModal(false);
          toast.success("E-mail enviado com sucesso, aguarde o contato de nossa administração.");
        }
      })
      .catch((_) => {
        setShowModal(false);
        toast.error("Ops, ocorreu um erro. Tente novamente.");
      });
  }

  return (
    <div className="ia-composer">
      <ModalAddIACredits
        show={showModal}
        onHide={() => setShowModal((prevState) => !prevState)}
        iaTokensValue={stringToFloat(user?.tokens_ai || 0)}
        onSubmit={onSubmitModalAddIaCredits}
      />

      <div className="ia-composer-body">
        {/* Botão toggle da sidebar (mobile) */}
        <button
          className={`ia-sidebar-toggle ${isSidebarOpen ? "is-active" : ""}`}
          onClick={onToggleSidebar}
          aria-label="Toggle menu"
          title="Abrir conversas"
        >
          <div className="ia-sidebar-toggle__icon">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>

        {!selectedChatId && (
          <select
            className="ia-composer__select"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="simple_question">Pergunta simples</option>
            <option value="contract_draft">Contrato</option>
            <option value="legal_petition">Petição</option>
            <option value="law_explanation">Explicação de lei</option>
            {/* <option value="client_support">Atendimento</option> */}
            <option value="legal_calculation">Cálculo</option>
          </select>
        )}

        <textarea
          ref={textareaRef}
          className="ia-composer__textarea"
          rows={2}
          placeholder={
            selectedChatId
              ? "Digite sua mensagem…"
              : "Descreva sua pergunta para iniciar a conversa…"
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button
          className="ia-composer__send"
          onClick={handleSend}
          disabled={isSending || !text.trim()}
        >
          {isSending ? "Enviando…" : "Enviar"}
        </button>
      </div>

      <div className="ia-composer-footer">
        <span>
          A IA EasyVog pode cometer erros. Verifique as informações importantes.
        </span>
      </div>
    </div>
  );
}
